import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import type { LucideIcon } from 'lucide-react';

/* ─── Accent Color Config ─── */
const accentConfig = {
  emerald: {
    topBar: 'from-emerald-500 to-green-600',
    headerGradient: 'from-emerald-500/20 via-green-500/15 to-emerald-500/5',
    iconGradient: 'from-emerald-500 to-green-600',
    iconGlow: 'shadow-[0_0_0_4px_rgba(16,185,129,0.15)]',
    statAccent: 'text-emerald-600 dark:text-emerald-400',
    actionTint: '[&_button]:text-emerald-700 [&_button]:dark:text-emerald-300 [&_button]:border-emerald-300 [&_button]:dark:border-emerald-700 [&_button]:hover:bg-emerald-50 [&_button]:dark:hover:bg-emerald-950/40',
  },
  blue: {
    topBar: 'from-blue-500 to-indigo-600',
    headerGradient: 'from-blue-500/20 via-indigo-500/15 to-blue-500/5',
    iconGradient: 'from-blue-500 to-indigo-600',
    iconGlow: 'shadow-[0_0_0_4px_rgba(59,130,246,0.15)]',
    statAccent: 'text-blue-600 dark:text-blue-400',
    actionTint: '[&_button]:text-blue-700 [&_button]:dark:text-blue-300 [&_button]:border-blue-300 [&_button]:dark:border-blue-700 [&_button]:hover:bg-blue-50 [&_button]:dark:hover:bg-blue-950/40',
  },
  teal: {
    topBar: 'from-teal-500 to-cyan-600',
    headerGradient: 'from-teal-500/20 via-cyan-500/15 to-teal-500/5',
    iconGradient: 'from-teal-500 to-cyan-600',
    iconGlow: 'shadow-[0_0_0_4px_rgba(20,184,166,0.15)]',
    statAccent: 'text-teal-600 dark:text-teal-400',
    actionTint: '[&_button]:text-teal-700 [&_button]:dark:text-teal-300 [&_button]:border-teal-300 [&_button]:dark:border-teal-700 [&_button]:hover:bg-teal-50 [&_button]:dark:hover:bg-teal-950/40',
  },
  purple: {
    topBar: 'from-purple-500 to-violet-600',
    headerGradient: 'from-purple-500/20 via-violet-500/15 to-purple-500/5',
    iconGradient: 'from-purple-500 to-violet-600',
    iconGlow: 'shadow-[0_0_0_4px_rgba(168,85,247,0.15)]',
    statAccent: 'text-purple-600 dark:text-purple-400',
    actionTint: '[&_button]:text-purple-700 [&_button]:dark:text-purple-300 [&_button]:border-purple-300 [&_button]:dark:border-purple-700 [&_button]:hover:bg-purple-50 [&_button]:dark:hover:bg-purple-950/40',
  },
  amber: {
    topBar: 'from-amber-500 to-orange-600',
    headerGradient: 'from-amber-500/20 via-orange-500/15 to-amber-500/5',
    iconGradient: 'from-amber-500 to-orange-600',
    iconGlow: 'shadow-[0_0_0_4px_rgba(245,158,11,0.15)]',
    statAccent: 'text-amber-600 dark:text-amber-400',
    actionTint: '[&_button]:text-amber-700 [&_button]:dark:text-amber-300 [&_button]:border-amber-300 [&_button]:dark:border-amber-700 [&_button]:hover:bg-amber-50 [&_button]:dark:hover:bg-amber-950/40',
  },
  rose: {
    topBar: 'from-rose-500 to-pink-600',
    headerGradient: 'from-rose-500/20 via-pink-500/15 to-rose-500/5',
    iconGradient: 'from-rose-500 to-pink-600',
    iconGlow: 'shadow-[0_0_0_4px_rgba(244,63,94,0.15)]',
    statAccent: 'text-rose-600 dark:text-rose-400',
    actionTint: '[&_button]:text-rose-700 [&_button]:dark:text-rose-300 [&_button]:border-rose-300 [&_button]:dark:border-rose-700 [&_button]:hover:bg-rose-50 [&_button]:dark:hover:bg-rose-950/40',
  },
  slate: {
    topBar: 'from-slate-500 to-gray-600',
    headerGradient: 'from-slate-500/20 via-gray-500/15 to-slate-500/5',
    iconGradient: 'from-slate-500 to-gray-600',
    iconGlow: 'shadow-[0_0_0_4px_rgba(100,116,139,0.15)]',
    statAccent: 'text-slate-600 dark:text-slate-400',
    actionTint: '[&_button]:text-slate-700 [&_button]:dark:text-slate-300 [&_button]:border-slate-300 [&_button]:dark:border-slate-700 [&_button]:hover:bg-slate-50 [&_button]:dark:hover:bg-slate-950/40',
  },
} as const;

export type AccentColor = keyof typeof accentConfig;

/* ─── Stat Item ─── */
export interface PageLayoutStat {
  label: string;
  value: string | number;
  color?: string; // Tailwind text color class e.g. 'text-orange-600'
  // When set, the stat renders as a button — turning a passive count into a triage
  // entry point (e.g. "To review" jumps to the approvals queue). Backward compatible:
  // stats without it render as a plain div exactly as before.
  onClick?: () => void;
  ariaLabel?: string; // accessible label for the actionable stat (defaults to `${value} ${label}`)
}

/* ─── CVA for max-width wrapper ─── */
const maxWidthVariants = cva('mx-auto px-6', {
  variants: {
    maxWidth: {
      '5xl': 'max-w-5xl',
      '6xl': 'max-w-6xl',
      '7xl': 'max-w-7xl',
      '1600px': 'max-w-[1600px]',
      full: 'max-w-full',
    },
  },
  defaultVariants: {
    maxWidth: '7xl',
  },
});

/* ─── CVA for stats grid ─── */
const statsGridVariants = cva('gap-3', {
  variants: {
    columns: {
      2: 'grid grid-cols-2',
      3: 'grid grid-cols-3',
      4: 'grid grid-cols-2 lg:grid-cols-4',
      5: 'grid grid-cols-2 lg:grid-cols-5',
    },
  },
  defaultVariants: {
    columns: 4,
  },
});

/* ─── Props ─── */
export interface PageLayoutProps extends VariantProps<typeof maxWidthVariants> {
  title: string;
  description?: string;
  icon: LucideIcon;
  accentColor: AccentColor;
  stats?: PageLayoutStat[];
  statsColumns?: 2 | 3 | 4 | 5;
  compactStats?: boolean;
  headerActions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

/* ─── Component ─── */
export function PageLayout({
  title,
  description,
  icon: Icon,
  accentColor,
  stats,
  statsColumns,
  compactStats,
  maxWidth,
  headerActions,
  children,
  className,
}: PageLayoutProps) {
  const accent = accentConfig[accentColor];
  const resolvedColumns = statsColumns ?? (stats ? (stats.length <= 1 ? 2 : stats.length <= 3 ? stats.length as 2 | 3 : 4) : 4);
  const hasStats = stats && stats.length > 0;
  const inlineActions = headerActions && !hasStats;

  return (
    <div className={className} data-testid="page-layout">
      {/* Header card */}
      <div className="mx-4 sm:mx-6 mt-4 rounded-xl overflow-hidden border border-border shadow-sm bg-card">
        {/* Accent top bar */}
        <div className={`h-[3px] bg-gradient-to-r ${accent.topBar}`} />

        {/* Header content */}
        <div className={`bg-gradient-to-r ${accent.headerGradient}`}>
          <div className={`${maxWidthVariants({ maxWidth })} py-6`}>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              {/* Title row — full width when inline actions need to go to the end */}
              <div className={`flex flex-wrap items-center gap-4 min-w-0 ${inlineActions ? 'w-full' : ''}`}>
                <div className="flex items-center min-w-0 flex-1 basis-48">
                  <div
                    className={`w-14 h-14 bg-gradient-to-br ${accent.iconGradient} ${accent.iconGlow} rounded-xl flex items-center justify-center shrink-0 me-4`}
                    data-testid="page-layout-icon"
                  >
                    <Icon size={26} color="white" strokeWidth={2} />
                  </div>
                  <div className="min-w-0">
                    <h1 className="text-2xl font-bold text-foreground tracking-tight">{title}</h1>
                    {description && (
                      <p className="text-sm text-muted-foreground mt-0.5">{description}</p>
                    )}
                  </div>
                </div>

                {/* Inline actions — wraps below title when space is tight */}
                {inlineActions && (
                  <div className={`${accent.actionTint}`}>
                    {headerActions}
                  </div>
                )}
              </div>

              {/* Stats (right side on desktop) */}
              {hasStats && (
                <div className={`${statsGridVariants({ columns: resolvedColumns })} shrink-0`}>
                  {stats.map((stat, index) => {
                    const inner = (
                      <>
                        <div className={`${compactStats ? 'text-sm' : 'text-2xl'} font-bold ${stat.color || accent.statAccent}`}>
                          {stat.value}
                        </div>
                        <div className="text-xs text-muted-foreground">{stat.label}</div>
                      </>
                    );
                    const boxClass = `bg-card/80 backdrop-blur-sm shadow-sm rounded-lg ${compactStats ? 'p-2' : 'p-3'} text-center`;
                    return stat.onClick ? (
                      <button
                        key={index}
                        type="button"
                        onClick={stat.onClick}
                        aria-label={stat.ariaLabel ?? `${stat.value} ${stat.label}`}
                        className={`${boxClass} cursor-pointer transition-shadow transition-colors hover:bg-card hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40`}
                        data-testid="page-layout-stat"
                      >
                        {inner}
                      </button>
                    ) : (
                      <div
                        key={index}
                        className={boxClass}
                        data-testid="page-layout-stat"
                      >
                        {inner}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Header actions row (only when stats are present) */}
            {headerActions && hasStats && (
              <div className={`mt-4 ${accent.actionTint}`}>{headerActions}</div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={`${maxWidthVariants({ maxWidth })} py-8`}>
        {children}
      </div>
    </div>
  );
}

export default PageLayout;
